import React from 'react';
import { Trash2, Eye, RefreshCw } from 'lucide-react';
import { Card } from '../../atoms/Card';
import { Button } from '../../atoms/Button';
import { type Content } from '../../../services/contentsService';
import { getImageUrl } from '../../../utils';

const WEB_BASE_URL = process.env.REACT_APP_WEB_URL || 'http://localhost:3000';

const getContentUrl = (slug: string | undefined, contentId: number): string => {
  const baseUrl = WEB_BASE_URL.endsWith('/') ? WEB_BASE_URL.slice(0, -1) : WEB_BASE_URL;
  return slug ? `${baseUrl}/i/${slug}?ig=true` : `${baseUrl}/i/${contentId}?ig=true`;
};

export interface ContentCardProps {
  content: Content;
  deletingId: number | null;
  onViewReports: (content: Content) => void;
  onDeleteClick: (content: Content) => void;
  onTriggerConversions?: (content: Content) => void;
  triggeringConversionsId?: number | null;
}

export const ContentCard = ({
  content,
  deletingId,
  onViewReports,
  onDeleteClick,
  onTriggerConversions,
  triggeringConversionsId,
}: ContentCardProps) => {
  const contentUrl = getContentUrl(content.slug, content.id);
  const hasReports = (content.reportCount || 0) > 0;
  const triggeringConversions = triggeringConversionsId === content.id;

  const firstImage = content.images?.[0];
  const firstVideo = content.videos?.[0];
  const displayImage = firstImage
    ? getImageUrl(firstImage.low_quality || firstImage.original)
    : firstVideo?.low_quality
      ? getImageUrl(firstVideo.low_quality)
      : content.thumbnail
        ? getImageUrl(content.thumbnail)
        : getImageUrl(undefined);

  const handleCardClick = () => {
    window.open(contentUrl, '_blank');
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  const handleTriggerConversions = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTriggerConversions) {
      onTriggerConversions(content);
    }
  };

  return (
    <Card
      className={`${hasReports ? 'bg-red-50 border-red-200' : ''} cursor-pointer hover:shadow-lg transition-shadow`}
      onClick={handleCardClick}
    >
      <div className="flex flex-col sm:flex-row items-start gap-4">
        <div className="flex-shrink-0 w-full sm:w-24">
          <img
            src={displayImage}
            alt={content.title}
            className="w-full sm:w-24 h-48 sm:h-24 object-cover rounded-lg border border-gray-200"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        <div className="flex-1 min-w-0 w-full">
          <div className="mb-2">
            <h3 className="text-lg font-semibold text-gray-800 break-words">
              {content.title}
            </h3>
          </div>
          <p className="text-sm text-gray-600 mb-3 line-clamp-2 break-words">
            {content.content}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500">
              <span className="whitespace-nowrap">ID: {content.id}</span>
              <span className="whitespace-nowrap">
                Oluşturulma: {new Date(content.createdOn).toLocaleDateString('tr-TR')}
              </span>
              {hasReports && (
                <span className="px-2 py-1 bg-red-200 text-red-800 rounded font-semibold whitespace-nowrap">
                  {content.reportCount} Rapor
                </span>
              )}
              {content.isDraft && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded whitespace-nowrap">
                  Taslak
                </span>
              )}
              {content.isRemoved && (
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded whitespace-nowrap">
                  Silinmiş
                </span>
              )}
            </div>
            {/* Desktop buttons */}
            <div className="hidden sm:flex gap-2 flex-shrink-0 ml-auto">
              <Button
                variant="secondary"
                size="small"
                onClick={(e) => handleActionClick(e, () => onViewReports(content))}
                className="flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Raporlar
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={handleTriggerConversions}
                disabled={triggeringConversions || content.isRemoved}
                isLoading={triggeringConversions}
                className="flex items-center gap-2"
                title="Medya dönüşümlerini tetikle"
              >
                <RefreshCw className="w-4 h-4" />
                Dönüştür
              </Button>
              <Button
                variant="danger"
                size="small"
                onClick={(e) => handleActionClick(e, () => onDeleteClick(content))}
                disabled={deletingId === content.id || content.isRemoved}
                isLoading={deletingId === content.id}
                className="flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Sil
              </Button>
            </div>
          </div>
          {/* Mobile buttons */}
          <div className="flex flex-col sm:hidden gap-2 mt-3">
            <Button
              variant="secondary"
              size="small"
              onClick={(e) => handleActionClick(e, () => onViewReports(content))}
              className="flex items-center justify-center gap-2 w-full"
            >
              <Eye className="w-4 h-4" />
              Raporlar
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={handleTriggerConversions}
              disabled={triggeringConversions || content.isRemoved}
              isLoading={triggeringConversions}
              className="flex items-center justify-center gap-2 w-full"
              title="Medya dönüşümlerini tetikle"
            >
              <RefreshCw className="w-4 h-4" />
              Dönüştür
            </Button>
            <Button
              variant="danger"
              size="small"
              onClick={(e) => handleActionClick(e, () => onDeleteClick(content))}
              disabled={deletingId === content.id || content.isRemoved}
              isLoading={deletingId === content.id}
              className="flex items-center justify-center gap-2 w-full"
            >
              <Trash2 className="w-4 h-4" />
              Sil
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
